import { atom, createStore } from 'jotai';
import { atomWithDefault, atomWithReset, atomWithStorage, createJSONStorage } from 'jotai/utils';

import { getSessionData } from '@/modules/api';
import { getCookie, translations } from '@/modules/common';
import { BridgeType, ChatFeedbackType, ErrorType, MessageType, PageType, ShopifyCart } from '@/modules/models';

import {
  CHAT_ID_STORAGE,
  IS_TOOLTIP_CLOSED_STORAGE,
  OPEN_CHAT_STORAGE,
  SESSION_FINISH_TIME_STORAGE,
  TOKEN_STORAGE,
  TOOLTIP_SHOWN_PAGES,
} from './constants';

const atomWithLocalStorage = <T>(key: string, defaultValue: T) =>
  atomWithStorage(key, defaultValue, undefined, { getOnInit: true });

export const atomWithSessionStorage = <T>(key: string, initialValue: T) =>
  atomWithStorage<T>(
    key,
    initialValue,
    createJSONStorage(() => sessionStorage)
  );

export const store = createStore();

export const isFontsLoadedAtom = atom(false);

export const isUserAnsweredAtom = atom(false);
export const isMlAnsweredAtom = atom(false);
export const isUnauthorizedAtom = atom(false);
export const requestsQueueAtom = atom<
  {
    url: string;
    method: string;
    headers: HeadersInit;
    body?: string;
  }[]
>([]);

export const messageTextAtom = atom('');
export const isLoadingMessageAtom = atom(false);
export const messagesAtom = atom<MessageType[]>([]);

export const bridgeAtom = atomWithReset<BridgeType>({
  translations,
  closeChatWithEsc: true,
  isAlwaysOpenWSConnection: false,
} as BridgeType);

export const cartAtom = atom<{ isOpen: boolean; products: ShopifyCart[] }>({
  isOpen: false,
  products: [],
});

export const messageTimeoutAtom = atom<number>(-1);
export const chatFeedbackAtom = atom<ChatFeedbackType | null>(null);

// Api atoms

export const sessionAtom = atomWithDefault(async () => getSessionData());

// Local storage atoms

export const tokenAtom = (() => {
  const localToken = localStorage.getItem(TOKEN_STORAGE);
  let token = localToken ? JSON.parse(localToken) : '';

  const cookieToken = getCookie(TOKEN_STORAGE);

  if (cookieToken && token !== cookieToken) {
    token = cookieToken;
  }

  return atomWithLocalStorage(TOKEN_STORAGE, token);
})();

export const chatIdAtom = atomWithLocalStorage<number | null>(CHAT_ID_STORAGE, null);
export const sessionFinishTimeAtom = atomWithLocalStorage(SESSION_FINISH_TIME_STORAGE, 0);
export const mlVersionAtom = atom<string | null>(null);

export const errorAtom = atom<ErrorType | null>(null);

// Session storage atoms
export const isOpenChatAtom = atomWithStorage(
  OPEN_CHAT_STORAGE,
  false,
  createJSONStorage<boolean>(() => sessionStorage)
);
export const isResetChatAtom = atom<boolean>(false);
export const isTooltipClosedAtom = atomWithSessionStorage(IS_TOOLTIP_CLOSED_STORAGE, false);
export const isReadyForScrollAtom = atom(false);
export const tooltipPagesAtom = atomWithSessionStorage<Partial<Record<PageType, string>>>(TOOLTIP_SHOWN_PAGES, {});
